import { useNavigate } from 'react-router-dom'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { useQueueComposition, useToggleDeckLearning } from '../hooks/useDecks'
import LearningToggleSwitch from '../components/shared/LearningToggleSwitch'
import type { QueueSourceItem } from '../types'

// ── Skeleton ──────────────────────────────────────────────────────────────────

function QueueComposeSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 28, height: 16, borderRadius: 8, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 14, width: `${50 + i * 15}%`, borderRadius: 4, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ height: 11, width: '30%', borderRadius: 4, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <div style={{ width: 40, height: 11, borderRadius: 4, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
        </div>
      ))}
    </div>
  )
}

// ── Source row ────────────────────────────────────────────────────────────────

function SourceRow({
  source, onToggle, pending,
}: {
  source: QueueSourceItem
  onToggle: (id: string) => void
  pending: boolean
}) {
  const navigate = useNavigate()
  const active = source.is_active_for_learning
  const hasWork = source.due_count > 0 || source.new_count > 0

  const countLabel = (() => {
    if (!active) return null
    if (source.due_count > 0 && source.new_count > 0) {
      return `${source.due_count} review · ${source.new_count} new`
    }
    if (source.due_count > 0) return `${source.due_count} review`
    if (source.new_count > 0) return `${source.new_count} new`
    return 'All caught up'
  })()

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 0', borderBottom: '1px solid var(--border)',
        minHeight: 60, opacity: active ? 1 : 0.6,
        transition: 'opacity 0.2s',
      }}
    >
      {/* Toggle button — 44px tap target wraps the small visual switch */}
      <button
        onClick={() => !pending && onToggle(source.deck_id)}
        aria-label={`${active ? 'Pause' : 'Resume'} ${source.title}`}
        aria-pressed={active}
        style={{
          background: 'none', border: 'none', cursor: pending ? 'default' : 'pointer',
          padding: '14px 8px 14px 0', margin: '-14px -8px -14px 0',
          flexShrink: 0, lineHeight: 0,
        }}
      >
        <LearningToggleSwitch active={active} />
      </button>

      {/* Title + counts */}
      <div
        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        onClick={() => navigate(`/decks/${source.deck_id}`)}
      >
        <div style={{
          fontSize: 15, fontWeight: 500, color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {source.title}
        </div>
        {countLabel && (
          <div style={{ fontSize: 12, color: active && hasWork ? 'var(--accent)' : 'var(--text-secondary)', marginTop: 2 }}>
            {countLabel}
          </div>
        )}
      </div>

      {/* Card count badge */}
      <div style={{ flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {source.card_count} {source.card_count === 1 ? 'card' : 'cards'}
        </span>
      </div>
    </div>
  )
}

// ── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: 'var(--text-secondary)',
      padding: '20px 0 8px',
    }}>
      {children}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function QueueComposePage() {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useQueueComposition()
  const toggleLearning = useToggleDeckLearning()

  if (isLoading) {
    return (
      <div style={{ maxWidth: 560, padding: '0 0 48px' }}>
        <PageHeader />
        <QueueComposeSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div style={{ maxWidth: 560, padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 16, background: 'var(--danger-soft)', color: 'var(--danger)' }}>
          <AlertCircle size={24} />
        </div>
        <div>
          <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>Couldn't load queue sources</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>Check your connection and try again.</p>
        </div>
        <button
          onClick={() => void refetch()}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 50, fontWeight: 600, fontSize: 14, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', cursor: 'pointer', minHeight: 44 }}
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    )
  }

  const sources = data?.sources ?? []
  const systemSources = sources.filter((s) => s.is_system)
  const userSources = sources.filter((s) => !s.is_system)
  const allPaused = sources.length > 0 && sources.every((s) => !s.is_active_for_learning)
  const activeCount = sources.filter((s) => s.is_active_for_learning).length

  function handleToggle(deckId: string) {
    toggleLearning.mutate(deckId)
  }

  return (
    <div style={{ maxWidth: 560, padding: '0 0 48px' }}>
      <PageHeader />

      {/* Active count summary */}
      {sources.length > 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 4px' }}>
          {activeCount === sources.length
            ? `${sources.length} ${sources.length === 1 ? 'source' : 'sources'} active`
            : `${activeCount} of ${sources.length} sources active`}
        </p>
      )}

      {/* All-paused warning */}
      {allPaused && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 'var(--radius-md)',
          background: 'var(--danger-soft)', border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)',
          marginTop: 12,
        }}>
          <AlertCircle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0, lineHeight: 1.5 }}>
            All sources are paused — enable at least one deck to start a session.
          </p>
        </div>
      )}

      {/* Empty state */}
      {sources.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
          <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', margin: '0 0 8px' }}>No decks yet</p>
          <p style={{ fontSize: 14, margin: '0 0 20px' }}>Create a deck or add words from text to start studying.</p>
          <button
            onClick={() => navigate('/decks')}
            style={{ padding: '10px 24px', borderRadius: 50, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}
          >
            My decks
          </button>
        </div>
      )}

      {/* System decks group */}
      {systemSources.length > 0 && (
        <section>
          <SectionLabel>Courses</SectionLabel>
          {systemSources.map((s) => (
            <SourceRow
              key={s.deck_id}
              source={s}
              onToggle={handleToggle}
              pending={toggleLearning.isPending}
            />
          ))}
        </section>
      )}

      {/* User decks group */}
      {userSources.length > 0 && (
        <section>
          {systemSources.length > 0 && <SectionLabel>Your decks</SectionLabel>}
          {userSources.map((s) => (
            <SourceRow
              key={s.deck_id}
              source={s}
              onToggle={handleToggle}
              pending={toggleLearning.isPending}
            />
          ))}
        </section>
      )}

      {/* Footer note */}
      {sources.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 24, lineHeight: 1.5 }}>
          Pausing a deck doesn't affect its mastery data — its cards resume from where they left off when you re-enable it.
        </p>
      )}
    </div>
  )
}

// ── Page header (extracted to keep main component readable) ───────────────────

function PageHeader() {
  return (
    <div style={{ marginBottom: 20 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
        Study Queue
      </h1>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
        These decks feed your daily session. Toggle any deck to pause or resume it.
      </p>
    </div>
  )
}
